// Shift handoff brief — auto-generated one-page summary that the
// outgoing dispatcher hands to the incoming one. Pulls from current
// state: open issues, pending alerts, hot bag tally, drivers on
// break/lunch, what the next bank looks like.
//
// Triggered from a button in the ContextBar; renders in a Dialog so
// the dispatcher can read it, copy it to a printer, or email it.

import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Button, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PrintIcon from '@mui/icons-material/Print';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useIntel } from './IntelligenceContext';
import { ALERTS, SEVERITY_META } from './mockAlerts';
import { GATE_DETAILS } from './mockGateDetails';
import { DRIVERS } from './mockLiveState';
import { DRIVER_DETAILS } from './mockDriverDetails';
import { C } from '../styles';

function HandoffButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Tooltip title="Generate shift handoff brief">
        <Button
          onClick={() => setOpen(true)}
          startIcon={<AssignmentIcon sx={{ fontSize: 14 }} />}
          size="small"
          sx={{
            color: C.textSecondary,
            border: `1px solid ${C.border}`,
            borderRadius: 0.6,
            textTransform: 'uppercase',
            fontSize: '0.6rem',
            fontWeight: 700,
            letterSpacing: 0.5,
            px: 1, py: 0.4, minWidth: 0,
            '&:hover': { color: C.accent, borderColor: C.accent, backgroundColor: 'rgba(249, 182, 18, 0.06)' },
          }}
        >
          Handoff
        </Button>
      </Tooltip>
      {open && <HandoffDialog onClose={() => setOpen(false)} />}
    </>
  );
}

function HandoffDialog({ onClose }) {
  const intel = useIntel();
  const { dismissedAlertIds, demoTime } = intel;

  const summary = useMemo(() => buildSummary(dismissedAlertIds), [dismissedAlertIds]);

  // Plain-text version for clipboard / print
  const textVersion = useMemo(() => formatText(summary), [summary]);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(20, 20, 25, 0.97)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          border: `1px solid ${C.border}`,
          color: C.textPrimary,
        },
      }}
    >
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        borderBottom: `1px solid ${C.border}`, py: 1.5,
      }}>
        <AssignmentIcon sx={{ color: C.accent }} />
        <Box sx={{ flex: 1 }}>
          <Typography component="span" sx={{ color: C.accent, fontWeight: 700, fontSize: '1rem' }}>
            Shift Handoff Brief
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: C.textMuted, mt: 0.25 }}>
            Auto-generated as of {fmtClock(demoTime)} — share with incoming dispatcher.
          </Typography>
        </Box>
        <Tooltip title="Copy to clipboard">
          <IconButton onClick={() => navigator.clipboard?.writeText(textVersion)} size="small" sx={{ color: C.textMuted, '&:hover': { color: C.accent } }}>
            <ContentCopyIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Print">
          <IconButton onClick={() => window.print()} size="small" sx={{ color: C.textMuted, '&:hover': { color: C.accent } }}>
            <PrintIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <IconButton onClick={onClose} size="small" sx={{ color: C.textMuted, ml: 0.5 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
          <BriefSection title="Open Issues" tone="red">
            {summary.openIssues.length === 0 ? (
              <Empty>No open issues.</Empty>
            ) : (
              summary.openIssues.map((iss, i) => (
                <BriefRow key={i} primary={iss.label} secondary={iss.gateId ? `Gate ${iss.gateId}` : null} />
              ))
            )}
          </BriefSection>

          <BriefSection title="Pending Alerts" tone="gold">
            {summary.pendingAlerts.length === 0 ? (
              <Empty>No pending alerts.</Empty>
            ) : (
              summary.pendingAlerts.map((a, i) => (
                <BriefRow key={i}
                  badgeColor={SEVERITY_META[a.severity].color}
                  primary={a.title}
                  secondary={a.body}
                />
              ))
            )}
          </BriefSection>

          <BriefSection title="Hot Bag Tally" tone="ok">
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, px: 0 }}>
              <KpiBig value={summary.hotsTotal}  label="Total Hot" color={C.textPrimary} />
              <KpiBig value={summary.hotsMade}   label="Made"      color="#4CAF50" />
              <KpiBig value={summary.hotsMissed} label="Missed"    color={summary.hotsMissed > 0 ? '#FF0000' : C.textMuted} />
            </Box>
          </BriefSection>

          <BriefSection title="Drivers Off Floor" tone="grey">
            {summary.driversOff.length === 0 ? (
              <Empty>All drivers on the floor.</Empty>
            ) : (
              summary.driversOff.map(d => (
                <BriefRow key={d.id}
                  primary={`${d.name} (#${d.badge})`}
                  secondary={d.kind === 'on_lunch'
                    ? `On lunch · returns ${d.returnsAt}`
                    : d.kind === 'on_break'
                      ? `On break · returns ${d.returnsAt}`
                      : '—'}
                />
              ))
            )}
          </BriefSection>

          <BriefSection title="What's Coming" tone="cyan" full>
            <BriefRow
              primary={`Next bank window: ${summary.nextBank.window}`}
              secondary={`${summary.nextBank.expectedFlights} expected inbounds · ${summary.nextBank.expectedHots} hot connections projected`}
            />
            <BriefRow
              primary="Equipment status"
              secondary={`${summary.equipmentBroken} broken · ${summary.equipmentInUse} in use`}
            />
          </BriefSection>
        </Box>
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${C.border}`, py: 1, px: 2 }}>
        <Button onClick={onClose} sx={{ color: C.textSecondary, textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function BriefSection({ title, tone, full, children }) {
  const toneColor = tone === 'red'  ? '#FF0000'
                  : tone === 'gold' ? '#F9B612'
                  : tone === 'ok'   ? '#4CAF50'
                  : tone === 'cyan' ? '#00BCD4'
                  : C.textSecondary;
  return (
    <Box sx={{
      p: 1.5,
      borderRight: full ? 'none' : `1px solid ${C.border}`,
      borderBottom: `1px solid ${C.border}`,
      gridColumn: full ? '1 / -1' : 'auto',
    }}>
      <Typography sx={{
        fontSize: '0.6rem', color: toneColor, fontWeight: 800,
        letterSpacing: 0.7, textTransform: 'uppercase', mb: 0.85,
      }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {children}
      </Box>
    </Box>
  );
}

function BriefRow({ primary, secondary, badgeColor }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 0.75,
      px: 0.85, py: 0.5,
      borderLeft: `3px solid ${badgeColor || 'transparent'}`,
      backgroundColor: 'rgba(255, 255, 255, 0.025)',
      borderRadius: 0.4,
    }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.75rem', color: C.textPrimary, fontWeight: 600, lineHeight: 1.25 }}>
          {primary}
        </Typography>
        {secondary && (
          <Typography sx={{ fontSize: '0.65rem', color: C.textMuted, lineHeight: 1.3, mt: 0.15 }}>
            {secondary}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function KpiBig({ value, label, color }) {
  return (
    <Box sx={{
      textAlign: 'center', px: 0.75, py: 1,
      backgroundColor: 'rgba(255,255,255,0.025)',
      border: `1px solid ${C.border}`, borderRadius: 0.5,
    }}>
      <Typography sx={{
        fontSize: '1.6rem', color, fontWeight: 700,
        fontVariantNumeric: 'tabular-nums', lineHeight: 1,
      }}>{value}</Typography>
      <Typography sx={{
        fontSize: '0.55rem', color: C.textMuted, fontWeight: 700,
        letterSpacing: 0.6, textTransform: 'uppercase', mt: 0.4,
      }}>{label}</Typography>
    </Box>
  );
}

function Empty({ children }) {
  return (
    <Typography sx={{ fontSize: '0.7rem', color: C.textMuted, fontStyle: 'italic' }}>
      {children}
    </Typography>
  );
}

// ── Build the summary from current state ────────────────────────────

function buildSummary(dismissedAlertIds) {
  const openIssues = [];
  let hotsTotal = 0, hotsMade = 0, hotsMissed = 0;

  GATE_DETAILS.forEach(gd => {
    gd.issues.forEach(iss => openIssues.push({ ...iss, gateId: gd.gateId }));
    gd.connections.forEach(c => {
      if (c.isHot) {
        hotsTotal++;
        // "make confidence" >= 75 ≈ projected to make
        if (c.makeConfidence >= 75) hotsMade++;
        else hotsMissed++;
      }
    });
  });

  const pendingAlerts = ALERTS.filter(a => !dismissedAlertIds.has(a.id));

  const driversOff = [];
  DRIVERS.forEach(d => {
    if (d.status === 'on_lunch' || d.status === 'on_break') {
      const det = DRIVER_DETAILS.get(d.id);
      driversOff.push({
        id: d.id, name: d.name, badge: d.badge,
        kind: d.status,
        returnsAt: det?.current?.returnsAt || '—',
      });
    }
  });

  // Equipment counts (deferred to keep this self-contained if mockEquipment isn't loaded)
  let equipmentInUse = 0, equipmentBroken = 0;
  try {
    // Lazy require so build doesn't break if mockEquipment isn't bundled
    const { EQUIPMENT } = require('./mockEquipment');
    EQUIPMENT.forEach(e => {
      if (e.status === 'in_use') equipmentInUse++;
      if (e.status === 'broken') equipmentBroken++;
    });
  } catch {} // eslint-disable-line

  // Next bank — placeholder
  const nextBank = {
    window: '09:30 → 11:30',
    expectedFlights: 12,
    expectedHots: 4,
  };

  return {
    openIssues,
    pendingAlerts,
    hotsTotal, hotsMade, hotsMissed,
    driversOff,
    equipmentInUse, equipmentBroken,
    nextBank,
  };
}

function formatText(s) {
  const lines = [];
  lines.push('=== SHIFT HANDOFF BRIEF ===');
  lines.push('');
  lines.push(`OPEN ISSUES (${s.openIssues.length})`);
  s.openIssues.forEach(i => lines.push(`  - ${i.label}${i.gateId ? ` (Gate ${i.gateId})` : ''}`));
  lines.push('');
  lines.push(`PENDING ALERTS (${s.pendingAlerts.length})`);
  s.pendingAlerts.forEach(a => lines.push(`  - [${a.severity.toUpperCase()}] ${a.title}: ${a.body}`));
  lines.push('');
  lines.push(`HOT BAGS — total ${s.hotsTotal} · made ${s.hotsMade} · missed ${s.hotsMissed}`);
  lines.push('');
  lines.push(`DRIVERS OFF FLOOR (${s.driversOff.length})`);
  s.driversOff.forEach(d => lines.push(`  - ${d.name} (#${d.badge}) — ${d.kind.replace('_', ' ')} · returns ${d.returnsAt}`));
  lines.push('');
  lines.push(`NEXT BANK — ${s.nextBank.window} · ${s.nextBank.expectedFlights} inbounds · ${s.nextBank.expectedHots} hot connections projected`);
  lines.push(`EQUIPMENT — ${s.equipmentInUse} in use · ${s.equipmentBroken} broken`);
  return lines.join('\n');
}

function fmtClock(mins) {
  const h = Math.floor(mins / 60) % 24;
  const m = Math.floor(mins) % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export default HandoffButton;
